import { deleteWishlistItemAction } from "@/actions/wishlist";
import { useMarket } from "@app/_context";
import useAuth from "@features/auth/hooks/useAuth";
import { type WishlistItem } from "@repo/core/wishlist";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useCallback } from "react";
import { deleteWishlistItemFromLocalStorage } from "../utils/localWishlistStorage";

export default function useDeleteWishlistItem() {
  const { market } = useMarket().state;
  const { token, user } = useAuth();
  const queryClient = useQueryClient();

  const { mutate, mutateAsync, isPending, isSuccess } = useMutation({
    mutationFn: async (item: WishlistItem) => {
      if (!token) {
        if (user) return;
        return deleteWishlistItemFromLocalStorage(item);
      }

      if (!item.id) {
        return deleteWishlistItemFromLocalStorage(item);
      }

      return deleteWishlistItemAction({
        token,
        itemId: item.id,
        locale: market.serviceLocale,
      });
    },
    onSettled: async () => {
      await queryClient.invalidateQueries({ queryKey: ["wishlist"] });
      void queryClient.refetchQueries({ queryKey: ["wishlist"] });
    },
  });

  const deleteWishlistItem = useCallback(
    (item: WishlistItem) => {
      mutate(item);
    },
    [mutate],
  );

  const deleteWishlistItemAsync = useCallback(
    (item: WishlistItem) => {
      return mutateAsync(item);
    },
    [mutateAsync],
  );

  return {
    deleteWishlistItem,
    deleteWishlistItemAsync,
    isPending,
    isSuccess,
  };
}
